import React, { useState, useRef, useEffect } from "react";
import {Input} from "../../../components/ui/Input";
import {Button} from "../../../components/ui/Button";
import apiService from "services/api";

const ParticipantSearch = ({
  searchEmail,
  setSearchEmail,
  onAddParticipant,
  errors,
  isLoading,
}) => {
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);

  const handleSearch = async (value) => {
    setSearchEmail(value);
    if (!value || value.length < 2) {
      return [];
    }

    try {
      const response = await apiService.searchUsers(value);
      setSearchResults(response.data.users);
      setShowDropdown(true);
      setIsSearching(false);
    } catch (error) {
      console.error("Error searching users:", error);
      setSearchResults([]);
      setShowDropdown(false);
    }
  };

  const handleSelectUser = (user) => {
    onAddParticipant(user);
    setSearchEmail("");
    setSearchResults([]);
    setShowDropdown(false);
  };

  const handleAddByEmail = () => {
    if (searchEmail?.trim() && isValidEmail(searchEmail?.trim())) {
      // Create a new user object for email-only addition
      const newUser = {
        name: searchEmail?.split("@")?.[0], // Use the part before @ as name
        email: searchEmail?.trim(),
        isTemporary: true, // Mark as temporary user to be created
        avatar: null,
      };
      onAddParticipant(newUser);
      setSearchEmail("");
      setSearchResults([]);
      setShowDropdown(false);
    } else {
      alert("Please enter a valid email address.");
    }
  };

  const isValidEmail = (email) => {
    // Simple email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleKeyDown = (e) => {
    if (e?.key === "Enter") {
      e?.preventDefault();
      if (searchResults?.length > 0) {
        handleSelectUser(searchResults?.[0]);
      } else if (searchEmail?.trim()) {
        // If there are no search results but user entered an email, add by email
        handleAddByEmail();
      }
    } else if (e?.key === "Escape") {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef?.current && !searchRef?.current?.contains(event?.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={searchRef}>
      <div className="flex gap-2">
        <div className="flex-1">
          <Input
            label="Add Participant"
            type="email"
            placeholder="Enter email address or search by name"
            value={searchEmail}
            onChange={(e) => handleSearch(e?.target?.value)}
            onKeyDown={handleKeyDown}
            error={errors?.participants}
            disabled={isLoading}
            description="Search for existing users or add by email"
          />
        </div>
        <div className="flex items-end">
          <Button
            variant="outline"
            onClick={handleAddByEmail}
            disabled={!searchEmail?.trim() || isLoading}
            iconName="Plus"
            iconPosition="left"
            className="mb-1"
          >
            Add
          </Button>
        </div>
      </div>
      {/* Search Results Dropdown */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-elevated z-50 max-h-60 overflow-y-auto">
          {isSearching ? (
            <div className="p-4 text-center">
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm text-muted-foreground">
                  Searching...
                </span>
              </div>
            </div>
          ) : searchResults?.length > 0 ? (
            <div className="py-1">
              {searchResults?.map((user) => (
                <button
                  key={user?.id}
                  onClick={() => handleSelectUser(user)}
                  className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-muted transition-micro text-left"
                >
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    {user?.avatar ? (
                      <img
                        src={user?.avatar}
                        alt={user?.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-sm font-medium text-primary-foreground">
                        {user?.name?.charAt(0)?.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {user?.name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {user?.email}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : searchEmail?.length >= 2 ? (
            <div className="p-4 text-center">
              <p className="text-sm text-muted-foreground mb-2">
                No users found
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAddByEmail}
                iconName="Plus"
                iconPosition="left"
              >
                Add "{searchEmail}"
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ParticipantSearch;
